import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { BookOpen, Eye, EyeOff, AlertCircle } from 'lucide-react';

export default function AcademyLoginPage() {
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // If already logged in, redirect
  React.useEffect(() => {
    if (!authLoading && user) navigate('/academy/mi-perfil');
  }, [user, navigate, authLoading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      navigate('/academy/mi-perfil');
    } catch (err) {
      setError(err.message || 'Error de autenticación. Revisa tus credenciales.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030712] flex flex-col items-center justify-center px-4 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/academy" className="inline-flex items-center gap-3 group">
            <div className="w-12 h-12 rounded-2xl bg-indigo-600 flex items-center justify-center">
              <BookOpen size={22} className="text-white" />
            </div>
            <div className="text-left">
              <p className="text-xs text-[#94A3B8] tracking-widest uppercase">Neuralia369</p>
              <p className="text-lg font-black text-white leading-none">Academy</p>
            </div>
          </Link>
          <p className="text-[#94A3B8] text-sm mt-4">Accede a tu área de formación</p>
        </div>

        {/* Card */}
        <div className="bg-[rgba(8,15,32,0.92)] border border-[rgba(148,163,184,0.14)] rounded-2xl p-8 backdrop-blur">
          <h2 className="text-xl font-bold text-white mb-6">Iniciar sesión</h2>

          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-xl bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-400" data-testid="academy-login-error">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="academy-login-form">
            <div>
              <label className="block text-xs text-[#94A3B8] mb-1.5 font-medium">Correo electrónico</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                data-testid="academy-login-email"
                className="w-full bg-white/5 border border-[rgba(148,163,184,0.14)] rounded-xl px-4 py-3 text-white text-sm placeholder-[#94A3B8]/50 focus:outline-none focus:border-indigo-500 transition"
                placeholder="tu@email.com"
              />
            </div>

            <div>
              <label className="block text-xs text-[#94A3B8] mb-1.5 font-medium">Contraseña</label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  data-testid="academy-login-password"
                  className="w-full bg-white/5 border border-[rgba(148,163,184,0.14)] rounded-xl px-4 py-3 pr-12 text-white text-sm placeholder-[#94A3B8]/50 focus:outline-none focus:border-indigo-500 transition"
                  placeholder="••••••••"
                />
                <button type="button" onClick={() => setShowPwd(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-white transition">
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading}
              data-testid="academy-login-submit"
              className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold transition disabled:opacity-50 mt-2">
              {loading ? 'Accediendo...' : 'Entrar a Academy'}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-[rgba(148,163,184,0.14)] text-center space-y-2">
            <p className="text-sm text-[#94A3B8]">
              ¿Sin cuenta?{' '}
              <Link to="/academy" className="text-indigo-400 hover:text-indigo-300 font-medium">
                Ver el catálogo de cursos
              </Link>
            </p>
          </div>
        </div>

        {/* Demo hint */}
        <div className="mt-4 text-center">
          <p className="text-xs text-[#94A3B8]/60">
            Usuarios de prueba: <span className="font-mono">alumno@neuralia.academy</span> · <span className="font-mono">tutor@neuralia.academy</span>
          </p>
          <p className="text-xs text-[#94A3B8]/60 mt-0.5">Contraseña: <span className="font-mono">AcademyTest123!</span></p>
        </div>
      </div>
    </div>
  );
}
